import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "aos/dist/aos.css";
import "./Profile.css";
import { buttonStyleSm, formatCurrency } from "../../help/Utils";
import backArrow from "../../assets/back-arrow.png";
import ChangePassword from "./ChangePassword";
import DownloadMyData from "./DownloadMyData";
import { BiSupport } from "react-icons/bi";
import { FiDownload, FiEdit } from "react-icons/fi";
import {
  getUserProfile,
  updateUserProfile,
  UserProfile,
} from "../../API/profileAPI";
import { getUserPayments, Payment } from "../../API/paymentAPI";
import { toast } from "react-toastify";
import { formatDateMMDDYYYY } from "../../utils/dateFormatting";
import { initialsFromName } from "../../utils/userInitials";

const Profile: React.FC = () => {
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);

  // User profile data
  const [profileData, setProfileData] = useState<UserProfile | null>(null);

  // Payment history data
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
  });

  const profileInitials = initialsFromName(
    profileData?.fullName,
    profileData?.email,
  );
  const isGoogleUser = Boolean(profileData?.googleId);

  // Fetch user profile on component mount
  useEffect(() => {
    fetchUserProfile();
    fetchRecentPayments();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getUserProfile();
      setProfileData(response.data.data);
      setFormData({
        fullName: response.data.data.fullName || "",
        phoneNumber: response.data.data.phoneNumber || "",
      });
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Failed to load profile";
      setError(errorMessage);
      toast.error(errorMessage);
      console.error("Error fetching profile:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRecentPayments = async () => {
    try {
      setIsLoadingPayments(true);
      const paymentsData = await getUserPayments();
      // Get only the 3 most recent payments
      setPayments(paymentsData.slice(0, 3));
    } catch (err: any) {
      console.error("Error fetching payments:", err);
      // Don't show error toast for payments, just log it
      // Profile page should still work even if payments fail
    } finally {
      setIsLoadingPayments(false);
    }
  };

  // Input change handler (phone: numeric only, max 10 digits)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let next = value;
    if (name === "phoneNumber") {
      next = value.replace(/\D/g, "").slice(0, 10);
    }
    setFormData({ ...formData, [name]: next });
  };

  // Handle profile update
  const handleSaveProfile = async () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    // Validate form data
    if (!formData.fullName.trim()) {
      toast.error("Full name is required");
      return;
    }
    if (formData.phoneNumber && formData.phoneNumber.replace(/\D/g, "").length !== 10) {
      toast.error("Phone number must be 10 digits");
      return;
    }

    try {
      setIsUpdating(true);
      const response = await updateUserProfile({
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
      });
      setProfileData(response.data.data);
      setIsEditing(false);
      toast.success("Profile updated successfully!");
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Failed to update profile";
      toast.error(errorMessage);
      console.error("Error updating profile:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (profileData) {
      setFormData({
        fullName: profileData.fullName || "",
        phoneNumber: profileData.phoneNumber || "",
      });
    }
    setIsEditing(false);
  };

  // Show error state
  if (error && !profileData) {
    return (
      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
        <div className="custom-container">
          <Link to="/dashboard" className="font-bold flex items-center mb-4">
            <img src={backArrow} alt="back arrow" className="me-2" /> Back to
            Dashboard
          </Link>
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={fetchUserProfile}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page_main page_main-after-login mt-[100px] pt-[40px] bg-[#F7F8FA] pb-[100px]">
        <div className="custom-container">
          {/* Back Navigation */}
          <Link to="/dashboard" className="font-bold flex items-center">
            <img src={backArrow} alt="back arrow" className="me-2" /> Back to
            Dashboard
          </Link>

          <div className="lg:flex justify-between mt-8">
            <div className=" lg:w-[69%]  w-full">
              <div className="p-6 border border-gray-200 rounded-[12px] bg-white  mb-5">
                <div className="md:flex items-center justify-between">
                  <div className="flex">
                    <div
                      className="w-[80px] h-[80px] rounded-[50%] me-8 flex shrink-0 items-center justify-center bg-[#1B365D] text-white text-[26px] font-semibold tracking-wide"
                      aria-label="Profile avatar"
                    >
                      {profileInitials || "U"}
                    </div>
                    <div className="md:w-auto w-[75%]">
                      {isLoading && !profileData ? (
                        <>
                          <div className="h-8 w-40 rounded bg-slate-200 animate-pulse mb-2" />
                          <div className="h-5 w-52 rounded bg-slate-200 animate-pulse mb-2" />
                        </>
                      ) : (
                        <>
                          <h4 className="md:text-2xl text-lg md:leading-8 leading-4 font-normal">
                            {profileData?.fullName}
                          </h4>
                          <span>{profileData?.email}</span> <br />
                        </>
                      )}
                      <label className="text-[12px] py-1 px-2 text-[#1B365D] bg-[#DBEAFE] rounded-[50px]">
                        Active Member
                      </label>
                    </div>
                  </div>
                  <div className="md:mt-0 mt-5 flex gap-2">
                    {isEditing && (
                      <button
                        className="flex items-center justify-center text-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        onClick={handleCancelEdit}
                        disabled={isUpdating}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      className="flex items-center justify-center text-center"
                      style={buttonStyleSm}
                      onClick={handleSaveProfile}
                      disabled={isUpdating || (isLoading && !profileData)}
                    >
                      {isUpdating ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white me-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiEdit className="me-4 text-[24px]" />
                          {isEditing ? "Save Profile" : "Edit Profile"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Personal Details */}
              <div className="p-6 border border-gray-200 rounded-[12px] bg-white  mb-5 personal_details">
                <h5 className="text-xl font-normal mb-5">Personal Details</h5>
                {isLoading && !profileData ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="h-4 w-24 rounded bg-slate-200 animate-pulse mb-2" />
                      <div className="h-10 w-full rounded bg-slate-200 animate-pulse" />
                    </div>
                    <div>
                      <div className="h-4 w-28 rounded bg-slate-200 animate-pulse mb-2" />
                      <div className="h-10 w-full rounded bg-slate-200 animate-pulse" />
                    </div>
                    <div>
                      <div className="h-4 w-16 rounded bg-slate-200 animate-pulse mb-2" />
                      <div className="h-10 w-full rounded bg-slate-200 animate-pulse" />
                      <div className="h-3 w-36 rounded bg-slate-200 animate-pulse mt-2" />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[14px] text-[#374151]">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        className={`block border w-full mt-1 p-2 rounded-md ${
                          !isEditing
                            ? "bg-gray-50 cursor-not-allowed"
                            : "bg-white"
                        }`}
                        value={formData.fullName}
                        onChange={handleChange}
                        disabled={!isEditing}
                      />
                    </div>
                    <div>
                      <label className="text-[14px] text-[#374151]">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phoneNumber"
                        inputMode="numeric"
                        maxLength={10}
                        placeholder="10 digits"
                        className={`block border w-full mt-1 p-2 rounded-md ${
                          !isEditing
                            ? "bg-gray-50 cursor-not-allowed"
                            : "bg-white"
                        }`}
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        disabled={!isEditing}
                      />
                    </div>
                    <div>
                      <label className="text-[14px] text-[#374151]">Email</label>
                      <input
                        type="email"
                        className="block border w-full mt-1 p-2 rounded-md bg-gray-50 cursor-not-allowed"
                        value={profileData?.email || ""}
                        disabled
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Email cannot be changed
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Invoices */}
              <div className="p-6 border border-gray-200 rounded-[12px] bg-white  mb-5">
                <div className="flex justify-between items-center mb-5">
                  <h5 className="text-xl font-normal ">Recent Payments</h5>
                  <Link
                    to="/payment-history"
                    className="text-blue-600 hover:underline"
                  >
                    View All
                  </Link>
                </div>

                {isLoadingPayments ? (
                  <div className="space-y-4">
                    {[0, 1, 2].map((item) => (
                      <div
                        key={item}
                        className="border border-[#E5E7EB] p-4 rounded-md"
                      >
                        <div className="h-6 w-28 rounded bg-slate-200 animate-pulse mb-3" />
                        <div className="h-4 w-56 rounded bg-slate-200 animate-pulse mb-3" />
                        <div className="h-6 w-20 rounded-full bg-slate-200 animate-pulse" />
                      </div>
                    ))}
                  </div>
                ) : payments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No payment history available</p>
                    <p className="text-sm mt-2">
                      Your payment transactions will appear here
                    </p>
                  </div>
                ) : (
                  payments.map((payment) => {
                    return (
                      <div
                        key={payment.id}
                        className="border border-[#E5E7EB] md:flex justify-between items-center p-4 rounded-md mb-4 last:mb-0"
                      >
                        <div className="flex-1">
                          <p className="font-semibold text-lg">
                            {formatCurrency(payment.amount)}
                          </p>
                          <label className="text-[14px] text-[#4B5563]">
                            <span
                              className="font-semibold"
                              style={{ color: "#1B365D" }}
                            >
                              {payment.package.name}
                            </span>
                            {" - "}
                            {formatDateMMDDYYYY(payment.createdAt)}
                          </label>
                          {/* {payment.stripeSessionId && (
                            <p className="text-xs text-gray-400 mt-1">
                              Session:{" "}
                              {payment.stripeSessionId.substring(0, 20)}
                              ...
                            </p>
                          )} */}
                        </div>
                        <label
                          className={`text-[12px] py-1 px-3 rounded-[50px] md:my-0 my-3 inline-block font-medium ${
                            payment.status === "succeeded" ||
                            payment.status === "completed"
                              ? "text-[#166534] bg-[#DCFCE7]"
                              : payment.status === "pending"
                              ? "text-[#854D0E] bg-[#FEF3C7]"
                              : "text-[#991B1B] bg-[#FEE2E2]"
                          }`}
                        >
                          {payment.status.charAt(0).toUpperCase() +
                            payment.status.slice(1)}
                        </label>
                        <div className="flex items-center md:ml-4">
                          {(payment.status === "succeeded" ||
                            payment.status === "completed") && (
                            <button
                              className="flex items-center text-blue-600 hover:text-blue-800 cursor-pointer"
                              onClick={() =>
                                toast.info(
                                  "Receipt download feature coming soon!"
                                )
                              }
                            >
                              <FiDownload className="text-[24px] me-2" />
                              <span className="hidden md:inline">
                                Download Receipt
                              </span>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right Side */}
            <div className=" lg:w-[30%] w-full">
              {!isGoogleUser && (
                <div className="p-6 border border-gray-200 rounded-[12px] bg-white mb-5">
                  <ChangePassword />
                </div>
              )}
              <DownloadMyData />
              <div className="p-6 border border-gray-200 rounded-[12px] bg-white ">
                <h5 className="text-xl font-normal mb-5">Support & Help</h5>
                <button
                  type="button"
                  onClick={() => window.open("mailto:support@letsgetvisa.com", "_blank")}
                  className="mt-5 w-full border border-[#E5E7EB] rounded-md flex items-center p-3 text-[16px] bg-white hover:bg-[#F9FAFB] cursor-pointer"
                >
                  <BiSupport className="me-4 text-[20px]" />
                  Contact Support Team
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Profile;
